import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiService } from 'src/app/shared/services/api.service';
import { NotificationRecipients, RecipientRole, RecipientUser } from 'src/app/shared/models/recipients';

@Component({
  selector: 'urban-recipient-picker',
  standalone: false,
  templateUrl: './recipient-picker.component.html',
  styleUrls: ['./recipient-picker.component.scss']
})
export class RecipientPickerComponent implements OnInit {
  // Setter, not a plain field: the parent replaces the value on discard and
  // after a save that falls back to the domain default, and the selects must
  // follow instead of keeping the state they were born with.
  @Input()
  public set value(value: NotificationRecipients) {
    this.selectedRoleIds = [...(value?.roles ?? [])];
    this.selectedUserIds = [...(value?.users ?? [])];
    if (!this.loading) {
      this.pruneMissing();
    }
  }

  @Input() public disabled: boolean = false;
  /** What an empty selection means for this list; the alerts' text by default. */
  @Input() public emptyText: string = 'RECIPIENTS.EMPTY';
  @Output() public valueChange: EventEmitter<NotificationRecipients> = new EventEmitter();

  public roles: RecipientRole[] = [];
  public users: RecipientUser[] = [];
  public selectedRoleIds: string[] = [];
  public selectedUserIds: string[] = [];
  public loading: boolean = true;

  private destroyRef: DestroyRef = inject(DestroyRef);

  constructor(private apiService: ApiService) { }

  ngOnInit(): void {
    this.loadCandidates();
  }

  public get isEmpty(): boolean {
    return this.selectedRoleIds.length === 0 && this.selectedUserIds.length === 0;
  }

  public userLabel(user: RecipientUser): string {
    const name: string = `${user.Firstname ?? ''} ${user.Lastname ?? ''}`.trim();
    return name.length > 0 ? name : user.Username;
  }

  public onRolesChange(roleIds: string[]): void {
    this.selectedRoleIds = roleIds;
    this.emit();
  }

  public onUsersChange(userIds: string[]): void {
    this.selectedUserIds = userIds;
    this.emit();
  }

  private loadCandidates(): void {
    this.apiService.getRecipientCandidates().pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        this.roles = res?.Roles ?? [];
        this.users = res?.Users ?? [];
        this.loading = false;
        this.pruneMissing();
      });
  }

  // A recipient deleted since the routing was saved is dropped from the
  // selection and reported up, so saving cleans the stale reference.
  private pruneMissing(): void {
    const roleIds: Set<string> = new Set(this.roles.map(role => role.Id));
    const userIds: Set<string> = new Set(this.users.map(user => user.Id));

    const roles: string[] = this.selectedRoleIds.filter(id => roleIds.has(id));
    const users: string[] = this.selectedUserIds.filter(id => userIds.has(id));

    if (roles.length !== this.selectedRoleIds.length || users.length !== this.selectedUserIds.length) {
      this.selectedRoleIds = roles;
      this.selectedUserIds = users;
      this.emit();
    }
  }

  private emit(): void {
    this.valueChange.emit({
      roles: [...this.selectedRoleIds],
      users: [...this.selectedUserIds]
    });
  }
}
